<template>
  <div>
    <page-help
        page="routes" title="help_routes_title" body="help_routes_body"
        :points="['help_routes_p1', 'help_routes_p2']" next="help_routes_next"
    ></page-help>
    <v-card>
      <v-card-title>
      <v-icon color="primary">
        mdi-road-variant
      </v-icon>
        <span class="px-2">{{ $t('routes') }}</span>
        <v-spacer></v-spacer>
        <v-sheet
            elevation="3"
            rounded="lg"
            class="text-center mx-auto">
            <div class="mx-4">
                <v-radio-group
                v-model="routes_type"
                row
                >
                    <v-radio
                        :label="$t('all_routes')"
                        value="all"
                    ></v-radio>
                    <v-radio
                        :label="$t('morning_routes')"
                        value="morning"
                    ></v-radio>
                    <v-radio
                        :label="$t('afternoon_routes')"
                        value="afternoon"
                    ></v-radio>
                </v-radio-group>
            </div>
        </v-sheet>
        <v-spacer></v-spacer>
        <v-menu
            offset-y>
            <template v-slot:activator="{ on, attrs }">
                <v-btn depressed
                :disabled="!activationStore.isActivated"
                class="mx-2"
                v-bind="attrs"
                v-on="on"
                color="primary">
                    {{ $t('create') }}
                    <v-icon right dark> mdi-plus-thick </v-icon>
                </v-btn>
            </template>
            <v-list>
                <v-list-item
                v-for="(item, index) in menu_items"
                :key="index"
                :value="index"
                @click="createRoute(item.title)"
                >
                <v-list-item-icon>
                    <v-icon>{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
        <activation-tool-tip model="routes"></activation-tool-tip>
      </v-card-title>
      <v-data-table
        v-model="selected"
        show-select
        :options.sync="tableOptions"
:footer-props="footerProps"
        :no-data-text="$t('no_routes_yet')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        loading-text="Loading... Please wait"
        :headers="translatedHeaders"
        :items="displayedRoutes"
        :search="debouncedSearch"
      >
        <template v-slot:top>
          <bulk-action-bar
            :count="selected.length"
            :running="bulkRunning"
            :done="bulkDone"
            :total="bulkTotal"
            :progress="bulkProgress"
            @clear="clearSelection"
          >
            <v-btn text small @click="bulkDelete">
              <v-icon small class="me-1">mdi-delete</v-icon>
              {{ $t('delete_selected') }}
            </v-btn>
          </bulk-action-bar>
          <v-text-field
            v-model="search"
            :label="$t('Search')"
            class="mx-4"
          ></v-text-field>
        </template>

        <!-- A route with no trip on it carries nobody, and a trip with no
             driver runs for nobody. Neither was visible here, so the only way
             to find either was to open the trips screen and read every row. -->
        <template v-slot:item.trips="{ item }">
          <v-chip
            small
            dark
            :color="tripsFor(item).length ? 'success' : 'warning'"
            @click="goToTrips()"
          >
            {{ tripsFor(item).length || $t('no_trips') }}
          </v-chip>
        </template>
        <!-- the point of a route is the children on it, which the list never
             said; the count opens the names -->
        <template v-slot:item.students="{ item }">
          <v-chip
            small
            dark
            :color="studentCount(item) ? 'primary' : 'warning'"
            @click="showStudents(item)"
          >
            {{ studentCount(item) }}
          </v-chip>
        </template>
        <template v-slot:item.drivers="{ item }">
          <span v-if="driversFor(item).length">
            <v-chip
              v-for="driver in driversFor(item)"
              :key="driver.id"
              class="ma-1"
              x-small
              dark
              color="info"
              @click="viewDriver(driver)"
            >
              {{ driver.name }}
            </v-chip>
          </span>
          <v-chip v-else x-small dark color="error">{{ $t('no_driver') }}</v-chip>
        </template>
        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>
        <template v-slot:item.is_morning="{ item }">
          <v-chip :color="getTimeColor(item.is_morning)" dark>
            {{ getTime(item.is_morning) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mr-4" @click="viewRoute(item)">
                mdi-eye
              </v-icon>
            </template>
            <span>{{ $t('view_route') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="editRoute(item, 'duplicate-new')">
                mdi-content-duplicate
              </v-icon>
            </template>
            <span>{{ $t('duplicate_as_new') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="item.is_morning" v-bind="attrs" v-on="on" small class="ml-2" @click="editRoute(item, 'duplicate-reverse')">
                mdi-moon-waning-crescent
              </v-icon>
            </template>
            <span>{{ $t('duplicate_as_afternoon') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="!item.is_morning" v-bind="attrs" v-on="on" small class="ml-2" @click="editRoute(item, 'duplicate-reverse')">
                mdi-white-balance-sunny
              </v-icon>
            </template>
            <span>{{ $t('duplicate_as_morning') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="ml-2" @click="deleteRoute(item, displayedRoutes.indexOf(item))">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete_route') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
    <bulk-result-dialog
      v-model="bulkResultDialog"
      :title="bulkResultTitle"
      :failures="bulkFailures"
    ></bulk-result-dialog>
    <students-on-dialog
      v-model="studentsDialog"
      scope="route"
      :subject="studentsSubject"
    ></students-on-dialog>
  </div>
</template>

<script>
import ActivationToolTip from "@/components/ActivationToolTip";
import PageHelp from "@/components/PageHelp";
import { activationStore } from "@/utils/helpers";

import tableState from "@/mixins/tableState";
import bulkActions from "@/mixins/bulkActions";
import BulkActionBar from "@/components/BulkActionBar";
import BulkResultDialog from "@/components/BulkResultDialog";
import StudentsOnDialog from "@/components/StudentsOnDialog";

export default {
  mixins: [tableState, bulkActions],
  components: {
    PageHelp,
    BulkActionBar,
    BulkResultDialog,
    StudentsOnDialog,
    ActivationToolTip,
    activationStore,
  },
  setup() {
    return { activationStore }
  },
  data() {
    return {
        routes_type: "all",
        menu_items: [
          { title: 'morning', icon: 'mdi-weather-sunset-up' },
          { title: 'afternoon', icon: 'mdi-weather-sunset-down' },
        ],
      routes: [],
      displayedRoutes: [],
      isLoading: false,
      tableStateKey: "routes",
      trips: [],
      studentCounts: {},
      studentsDialog: false,
      studentsSubject: null,
      headers: [
        { text: "ID", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Stops", value: "stops_count" },
        { text: "students", value: "students", sortable: false },
        { text: "trips", value: "trips", sortable: false },
        { text: "drivers", value: "drivers", sortable: false },
        { text: "Time", value: "is_morning" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => {
        return {
          ...header,
          text: this.$t(header.text.toLowerCase().replace(' ', '_'))
        };
      });
    },
  },
  watch: {
    routes_type: function (newVal, oldVal) {
      this.filterRoutes(newVal);
    }
  },
  mounted() {
    this.loadRoutes();
    this.loadTrips();
    this.loadStudentCounts();
  },
  methods: {
    //one request for the whole table rather than one per row
    loadStudentCounts() {
      axios
        .get("/school-students/assignments")
        .then((response) => {
          this.studentCounts = (response.data && response.data.routes) || {};
        })
        .catch(() => {
          //the rest of the table is still worth showing
        });
    },
    studentCount(route) {
      return this.studentCounts[route.id] || 0;
    },
    showStudents(route) {
      this.studentsSubject = route;
      this.studentsDialog = true;
    },
    //joined in the panel rather than added to /routes/all, which the apps use
    tripsFor(route) {
      return this.trips.filter((trip) => trip.route_id === route.id);
    },
    driversFor(route) {
      const drivers = [];
      this.tripsFor(route).forEach((trip) => {
        if (trip.driver && !drivers.some((driver) => driver.id === trip.driver.id)) {
          drivers.push(trip.driver);
        }
      });
      return drivers;
    },
    loadTrips() {
      axios
        .get("/trips/all")
        .then((response) => {
          this.trips = (response.data && response.data.activeTrips) || [];
        })
        .catch(() => {
          //the routes themselves still list; the two columns just stay empty
        });
    },
    goToTrips() {
      this.$router.push({ name: "trips" });
    },
    viewDriver(driver) {
      this.$router.push({ name: "view-driver", params: { user_id: driver.id } });
    },
    bulkDelete() {
      this.runBulk({
        items: this.selected,
        title: this.$t("delete_selected"),
        confirmText: this.$t("confirm_bulk_delete", { count: this.selected.length }),
        label: (route) => route.name,
        request: (route) => axios.delete(`/routes/${route.id}`),
        onSuccess: (route) => this.removeFromList(this.routes, route),
      });
    },
    filterRoutes(routes_type)
    {
        this.routes_type = routes_type;
        if(routes_type == "all")
        {
            this.displayedRoutes = this.routes;
        }
        else if(routes_type == "morning")
        {
            this.displayedRoutes = this.routes.filter(route => route.is_morning == 1);
        }
        else if(routes_type == "afternoon")
        {
            this.displayedRoutes = this.routes.filter(route => route.is_morning == 0);
        }
    },
    loadRoutes() {
      this.isLoading = true;
      this.routes = [];
      this.displayedRoutes = [];
      axios
        .get(`/routes/all`)
        .then((response) => {
          this.routes = response.data;
          this.displayedRoutes = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: this.$t("error_retrieving_routes"),
            type: 'error'
          });
          console.log(error);
          this.$notifyError(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    getTimeColor(is_morning) {
      return is_morning ? "success" : "warning";
    },
    getTime(is_morning) {
      return is_morning ? this.$t("morning") : this.$t("afternoon");
    },
    //Two routes of a school may not share a name, and the editor has no name
    //field of its own - the name is chosen here and never asked for again. So
    //a clash has to be caught in this prompt: finding out at save time would
    //mean losing a drawn route with no way to rename it.
    routeNameTaken(name) {
      const wanted = (name || "").trim().toLowerCase();
      return this.routes.some(
        (route) => (route.name || "").trim().toLowerCase() === wanted
      );
    },
    //"Route 1" -> "Route 1 (2)", so duplicating never opens with a name that
    //is already refused
    suggestRouteName(base) {
      const name = (base || "").trim();
      if (name && !this.routeNameTaken(name)) return name;
      for (let n = 2; n < 100; n++) {
        const candidate = `${name} (${n})`;
        if (!this.routeNameTaken(candidate)) return candidate;
      }
      return name;
    },
    routeNameError(value) {
      const name = (value || "").trim();
      if (!name) return this.$t("route_name_required");
      if (this.routeNameTaken(name)) return this.$t("route_name_taken");
      return null;
    },
    createRoute(title) {
      this.$swal
        .fire({
          title: this.$t("enter_route_name", { type: this.$t(title.toLowerCase()) }),
          input: "text",
            inputPlaceholder: this.$t("new_route"),
          showCancelButton: true,
          cancelButtonText: this.$t("cancel"),
          confirmButtonText: this.$t("ok"),
          inputValidator: (value) => this.routeNameError(value),
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$router.push({
              name: "create-route",
              params: { route_name: result.value.trim(), route_type: title },
            });
          }
        });
    },
    viewRoute(route) {
      this.$router.push({
        name: "view-route",
        params: { route_id: route.id},
      });
    },
    editRoute(route, action = "edit") {
      this.$swal
        .fire({
          title: this.$t("enter_route_name_simple"),
          input: "text",
          //the copy opens with a free name rather than the original's, which
          //the school's own routes already use
          inputValue: this.suggestRouteName(route.name),
          showCancelButton: true,
          cancelButtonText: this.$t("cancel"),
          confirmButtonText: this.$t("ok"),
          inputValidator: (value) => this.routeNameError(value),
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$router.push({
              name: "edit-route",
              params: { route_id: route.id, new_route_name: result.value.trim(), action: action },
            });
          }
        });

    },
    deleteRoute(route, index) {
      //say what goes with it: a route carrying children and running trips is
      //not the same decision as an empty one drawn by mistake
      const students = this.studentCount(route);
      const trips = this.tripsFor(route).length;

      this.$swal
        .fire({
          title: this.$t("delete_route"),
          text: students || trips
            ? this.$t("confirm_delete_route_in_use", {
                name: route.name,
                students: students,
                trips: trips,
              })
            : this.$t("confirm_delete_route", { name: route.name }),
          icon: "error",
          showCancelButton: true,
          confirmButtonText: this.$t("yes_delete"),
          cancelButtonText: this.$t("cancel"),
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deleteRouteServer(route.id, index);
          }
        });
    },
    deleteRouteServer(route_id, index) {
      axios
        .delete(`/routes/${route_id}`)
        .then((response) => {
          let idx = this.routes.findIndex((route) => route.id == route_id);
          this.routes.splice(idx, 1);
          this.filterRoutes(this.routes_type);
          this.$notify({
            title: "Success",
            text: this.$t("route_deleted"),
            type: "success",
          });
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: this.$t("error_deleting_routes"),
            type: 'error'
          });
          this.$notifyError(error);
        })
        .then(() => {
          //this.isDeleting = false;
        });
    },
  },
};
</script>
